import type { ComponentChildren } from 'preact'
import { Link, useLocation } from 'wouter'
import { ROUTES } from '../routes'
import { styled } from '../setup'
import { borders, colors, spacing, typography } from '../theme'

const Container = styled('div')`
  min-height: 100vh;
  background: ${colors.bgPage};
  box-shadow: inset 0 0 120px rgba(0,0,0,0.04);
`

const Nav = styled('nav')`
  background: ${colors.bgSurface};
  border-bottom: 1px solid ${colors.border};
  padding: ${spacing.lg} ${spacing['2xl']};
`

const NavContent = styled('div')`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.lg};
`

const NavTitle = styled('h1')`
  margin: 0;
  font-size: ${typography.fontSizeXl};
  font-weight: ${typography.fontWeightSemibold};
  color: ${colors.textPrimary};
`

const NavLinks = styled('div')`
  display: flex;
  gap: ${spacing.sm};
`

const NavLink = styled('a')<{ active: boolean }>`
  padding: ${spacing.sm} ${spacing.lg};
  border-radius: ${borders.radius};
  font-size: ${typography.fontSizeBase};
  font-weight: ${typography.fontWeightMedium};
  text-decoration: none;
  transition: background 0.2s, color 0.2s;

  background: ${(props) => (props.active ? colors.accentPrimary : 'transparent')};
  color: ${(props) => (props.active ? colors.accentText : colors.textSecondary)};

  &:hover {
    background: ${(props) => (props.active ? colors.accentHover : colors.bgSurfaceAlt)};
  }
`

const BackLink = styled('a')`
  padding: ${spacing.sm} ${spacing.lg};
  border-radius: ${borders.radius};
  font-size: ${typography.fontSizeBase};
  font-weight: ${typography.fontWeightMedium};
  text-decoration: none;
  color: ${colors.textMuted};
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: ${colors.bgSurfaceAlt};
    color: ${colors.textSecondary};
  }
`

const Content = styled('main')`
  padding: ${spacing['2xl']};
  max-width: 1200px;
  margin: 0 auto;
`

interface GuardianLayoutProps {
  children: ComponentChildren
}

export function GuardianLayout({ children }: GuardianLayoutProps) {
  const [location] = useLocation()

  const isOnDashboard = location === ROUTES.GUARDIAN_DASHBOARD
  const isOnRequests = location === ROUTES.GUARDIAN_REQUESTS

  return (
    <Container>
      <Nav>
        <NavContent>
          <NavTitle>Guardian Dashboard</NavTitle>
          <NavLinks>
            <Link href={ROUTES.GUARDIAN_DASHBOARD}>
              <NavLink active={isOnDashboard}>Children</NavLink>
            </Link>
            <Link href={ROUTES.GUARDIAN_REQUESTS}>
              <NavLink active={isOnRequests}>Requests</NavLink>
            </Link>
            <Link href={ROUTES.PROFILE}>
              <BackLink>Back to Profile</BackLink>
            </Link>
          </NavLinks>
        </NavContent>
      </Nav>
      <Content>{children}</Content>
    </Container>
  )
}
